import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { CAT_FR, PROJECTS, T } from "@/data/content";
import { FadeUp, SectionLabel, BlurBackdrop, Portal } from "@/components/Reveal";
import ProjectModal from "@/components/ProjectModal";
import ShareMediaButton from "@/components/ShareMediaButton";
import { ArrowUpRight, Clapperboard } from "lucide-react";

const FILTERS = ["ALL", "COMMERCIAL", "EVENTS", "SOCIAL / DIGITAL"];

const VideoCard = ({ p, i, onOpen }) => (
    <motion.button
        data-testid={`allv-card-${p.id}`}
        data-cursor="VIEW"
        onClick={() => onOpen(p)}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.12 }}
        transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
        className="group relative block w-full overflow-hidden text-left"
    >
        <div className="relative aspect-video w-full overflow-hidden">
            <video
                className="h-full w-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.06]"
                poster={p.img}
                muted
                loop
                playsInline
                preload="none"
                onMouseEnter={(e) => {
                    const v = e.currentTarget;
                    v.muted = true;
                    v.play().catch(() => {});
                }}
                onMouseLeave={(e) => e.currentTarget.pause()}
            >
                <source src={p.video} type="video/mp4" />
                <source src={`${p.video.split(".mp4")[0]}.webm`} type="video/webm" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-ink/10 to-ink/20 opacity-90 transition-opacity duration-500 group-hover:opacity-100" />
            <div className="absolute inset-x-0 top-0 flex items-center justify-between p-4 font-mono text-[9px] tracking-[0.25em] text-cream/90 md:text-[10px]">
                <span>{p.category}</span>
                <span>{p.year}</span>
            </div>
            <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-4 md:p-5">
                <div className="translate-y-2 transition-transform duration-500 group-hover:translate-y-0">
                    <h3 className="font-display text-lg font-extrabold uppercase tracking-tight text-cream md:text-xl">
                        {p.title}
                    </h3>
                    {p.client && (
                        <p className="mt-1 font-mono text-[9px] tracking-[0.25em] text-violet">
                            {p.client}
                        </p>
                    )}
                </div>
                <ArrowUpRight
                    size={18}
                    className="translate-y-2 text-cream/60 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100"
                />
            </div>
        </div>
    </motion.button>
);

export default function AllVideos() {
    const { lang } = useLang();
    const [cat, setCat] = useState("ALL");
    const [active, setActive] = useState(null);
    const filtered = cat === "ALL" ? PROJECTS : PROJECTS.filter((p) => p.category === cat);

    // ouverture directe d'une vidéo via lien profond (ex: bio 6xo)
    useEffect(() => {
        const target = window.__openProject;
        if (!target) return;
        window.__openProject = null;
        const p = typeof target === "string" ? PROJECTS.find((x) => x.id === target) : target;
        if (p) setActive(p);
    }, []);

    return (
        <section
            id="all-videos"
            data-testid="all-videos-section"
            className="relative overflow-hidden bg-[#101014] pb-24 pt-32 text-cream md:pb-36 md:pt-44"
        >
            <BlurBackdrop src="/videos/work-sakura.jpg" tint="bg-[#101014]/75" />
            <div className="relative mx-auto max-w-[1600px] px-5 md:px-10">
                <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
                    <div>
                        <SectionLabel en="VIDEO ARCHIVE" fr="ARCHIVE VIDÉO" />
                        <FadeUp delay={0.1}>
                            <h1 className="mt-8 font-display text-[clamp(2.6rem,8vw,7rem)] font-extrabold uppercase leading-none tracking-[-0.02em]">
                                {tx(lang, T.work.allTitle)}
                                <span className="text-violet">.</span>
                            </h1>
                        </FadeUp>
                    </div>
                    <FadeUp delay={0.2}>
                        <p className="font-mono text-[9px] tracking-[0.3em] text-cream/40 md:text-[10px]">
                            {tx(lang, T.work.allNote)}
                        </p>
                    </FadeUp>
                </div>

                <FadeUp delay={0.1} className="mt-10 md:mt-14">
                    <div className="flex flex-wrap gap-2 md:gap-3" data-testid="allv-filters">
                        {FILTERS.map((c) => (
                            <button
                                key={c}
                                data-testid={`allv-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                onClick={() => setCat(c)}
                                className={`rounded-full border px-4 py-2 font-mono text-[10px] tracking-[0.2em] transition-all duration-300 md:px-5 md:text-[11px] ${
                                    cat === c
                                        ? "border-cream bg-cream text-ink"
                                        : "border-white/20 text-cream/60 hover:border-cream hover:text-cream"
                                }`}
                            >
                                {lang === "fr" ? CAT_FR[c] : c}
                            </button>
                        ))}
                    </div>
                </FadeUp>

                <motion.div
                    key={cat}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 md:mt-16 lg:grid-cols-3 md:gap-8"
                >
                    {filtered.map((p, i) => (
                        <div key={p.id} className="relative">
                            <VideoCard p={p} i={i} onOpen={setActive} />
                            <div className="absolute bottom-3 right-3 z-10" onClick={(e) => e.stopPropagation()}>
                                <ShareMediaButton
                                    kind="video"
                                    src={p.video}
                                    poster={p.img}
                                    title={`${p.title}${p.client ? ` — ${p.client}` : ""}`}
                                    target="#videos"
                                    testid={`share-video-${p.id}`}
                                />
                            </div>
                        </div>
                    ))}
                    {filtered.length === 0 && (
                        <div className="col-span-full flex items-center gap-3 border border-dashed border-cream/20 p-10 font-mono text-[10px] tracking-[0.3em] text-cream/40">
                            <Clapperboard size={14} />
                            {tx(lang, T.empty.work)}
                        </div>
                    )}
                </motion.div>
            </div>

            <Portal><AnimatePresence>
                {active && (
                    <ProjectModal project={active} onClose={() => setActive(null)} />
                )}
            </AnimatePresence></Portal>
        </section>
    );
}
